import React, {useState} from 'react'
import {Button, Input} from 'react-daisyui'
import {resetPassword} from 'aws-amplify/auth'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import clsx from 'clsx'
import {useForm} from 'react-hook-form'
import {
  trackFormSubmission,
  trackValidationError
} from '../../services/errorAnalytics'

export const SendResetPasswordCodeForm = ({
  onCodeSent
}: {
  onCodeSent?: Function
}) => {
  const {
    register,
    handleSubmit,
    formState: {errors}
  } = useForm()
  const [isSendingCode, setIsSendingCode] = useState(false)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Track validation errors
  const trackFieldValidationError = (
    fieldName: string,
    errorMessage: string
  ) => {
    trackValidationError('password_reset_request', fieldName, errorMessage)
  }

  const handleSendCode = async (data: any): Promise<void> => {
    setIsSendingCode(true)
    try {
      const caseInsensitiveEmail = data.email.toLowerCase()
      const response = await resetPassword({username: caseInsensitiveEmail})

      // Track successful form submission
      trackFormSubmission('password_reset_request', true)

      if (onCodeSent) {
        onCodeSent({response, data: {...data, email: caseInsensitiveEmail}})
      }
    } catch (error) {
      const errorMessage = error.message || 'An error occurred'

      // Track failed form submission
      trackFormSubmission('password_reset_request', false, errorMessage)

      setErrorMessage(errorMessage)
    } finally {
      setIsSendingCode(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSendCode)} aria-live="polite">
      {errorMessage && (
        <div className="text-red-500" role="alert" aria-live="assertive">
          {errorMessage}
        </div>
      )}
      <label className="label pt-6 pb-1" htmlFor="email">
        <span className="label-text">Email</span>
      </label>
      <div>
        <Input
          id="email"
          aria-invalid={errors.email ? 'true' : 'false'}
          aria-describedby="email-error"
          {...register('email', {required: true, pattern: /^\S+@\S+$/i})}
          className={'input-form' + (errors.email ? ' input-error' : '')}
          onBlur={e => {
            if (errors.email?.type === 'required') {
              trackFieldValidationError('email', 'Email is required')
            } else if (errors.email?.type === 'pattern') {
              trackFieldValidationError('email', 'Invalid email address')
            }
          }}
        ></Input>
        {errors.email?.type === 'required' && (
          <p
            id="email-error"
            className="text-red-500"
            role="alert"
            aria-live="assertive"
          >
            Email is required
          </p>
        )}
        {errors.email?.type === 'pattern' && (
          <p
            id="email-error"
            className="text-red-500"
            role="alert"
            aria-live="assertive"
          >
            Invalid email address
          </p>
        )}
      </div>
      <Button
        className="w-full mt-8"
        color="secondary"
        type="submit"
        aria-busy={isSendingCode}
        aria-disabled={isSendingCode}
      >
        Send Code
      </Button>
      <div
        className={clsx({hidden: !isSendingCode})}
        aria-hidden={!isSendingCode}
      >
        <div className="pt-4">
          <LoadingSpinner size={LoadingSpinnerSize.Small} />
        </div>
      </div>
    </form>
  )
}

export default SendResetPasswordCodeForm
